import React, { useState } from "react";
import { useApp } from "../../context/AppContext";
import api from "../../api";
import { toast } from "sonner";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Plus, Pencil, Trash2, BookOpen } from "lucide-react";

const TYPES = ["asset", "liability", "equity", "revenue", "expense"];
export const TYPE_COLORS = {
  asset: "bg-blue-500/15 text-blue-500 border-blue-500/30", liability: "bg-amber-500/15 text-amber-500 border-amber-500/30",
  equity: "bg-purple-500/15 text-purple-500 border-purple-500/30", revenue: "bg-emerald-500/15 text-emerald-500 border-emerald-500/30",
  expense: "bg-red-500/15 text-red-500 border-red-500/30",
};

export const AccountsTab = ({ accounts, reload }) => {
  const { t, fmt, entity } = useApp();
  const [editor, setEditor] = useState(null);
  const [mast, setMast] = useState(null);
  const [q, setQ] = useState("");

  const save = async () => {
    try {
      const body = { ...editor, entity_id: entity };
      if (editor.id) await api.put(`/accounts/${editor.id}`, body); else await api.post("/accounts", body);
      toast.success(t("msg.saved")); setEditor(null); reload();
    } catch (e) { const d = e.response?.data?.detail; toast.error(typeof d === "string" ? d : t("msg.error")); }
  };
  const remove = async (a) => {
    try { await api.delete(`/accounts/${a.id}`); toast.success(t("msg.deleted")); reload(); }
    catch (e) { const d = e.response?.data?.detail; toast.error(typeof d === "string" ? d : t("msg.error")); }
  };
  const openMast = async (a) => {
    try { const r = await api.get(`/ledger/account/${a.code}/movements?entity_id=${entity}`); setMast(r.data); } catch { toast.error(t("msg.error")); }
  };
  const rows = accounts.filter((a) => !q || `${a.code} ${a.name}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-4" data-testid="accounts-tab">
      <div className="flex flex-wrap items-center gap-2">
        <Input className="max-w-xs" placeholder={t("ord.searchAccount")} value={q} onChange={(e) => setQ(e.target.value)} data-testid="account-search-input" />
        <div className="flex-1" />
        <Button onClick={() => setEditor({ code: "", name: "", type: "expense" })} data-testid="new-account-button"><Plus className="h-4 w-4 mr-2" />{t("ord.newAccount")}</Button>
      </div>
      <Card><CardContent className="p-0">
        <Table>
          <TableHeader><TableRow><TableHead>{t("ledger.code")}</TableHead><TableHead>{t("common.name")}</TableHead><TableHead>{t("common.type")}</TableHead><TableHead className="w-28" /></TableRow></TableHeader>
          <TableBody>
            {rows.map((a) => (
              <TableRow key={a.code} data-testid={`account-${a.code}`}>
                <TableCell className="font-mono text-xs">{a.code}</TableCell>
                <TableCell className="text-sm">{a.name}</TableCell>
                <TableCell><Badge className={TYPE_COLORS[a.type]}>{t(`ord.type.${a.type}`)}</Badge></TableCell>
                <TableCell><div className="flex gap-1">
                  <Button size="icon" variant="ghost" className="h-7 w-7" title={t("ord.mastrino")} onClick={() => openMast(a)} data-testid={`mastrino-${a.code}`}><BookOpen className="h-3.5 w-3.5" /></Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditor({ id: a.id, code: a.code, name: a.name, type: a.type })} data-testid={`edit-account-${a.code}`}><Pencil className="h-3.5 w-3.5" /></Button>
                  {!a.system && <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => remove(a)} data-testid={`delete-account-${a.code}`}><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>}
                </div></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>

      <Dialog open={!!editor} onOpenChange={(v) => !v && setEditor(null)}>
        <DialogContent className="max-w-md" data-testid="account-dialog">
          <DialogHeader><DialogTitle>{editor?.id ? t("ord.editAccount") : t("ord.newAccount")}</DialogTitle></DialogHeader>
          {editor && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div><Label>{t("ledger.code")}</Label><Input value={editor.code} disabled={!!editor.id} onChange={(e) => setEditor({ ...editor, code: e.target.value })} data-testid="account-code-input" /></div>
                <div className="col-span-2"><Label>{t("common.name")}</Label><Input value={editor.name} onChange={(e) => setEditor({ ...editor, name: e.target.value })} data-testid="account-name-input" /></div>
              </div>
              <div><Label>{t("common.type")}</Label>
                <Select value={editor.type} onValueChange={(v) => setEditor({ ...editor, type: v })}><SelectTrigger data-testid="account-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{TYPES.map((x) => <SelectItem key={x} value={x}>{t(`ord.type.${x}`)}</SelectItem>)}</SelectContent></Select></div>
              <Button className="w-full" onClick={save} disabled={!editor.code || !editor.name} data-testid="account-save-button">{t("common.save")}</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!mast} onOpenChange={(v) => !v && setMast(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="mastrino-dialog">
          <DialogHeader><DialogTitle>{t("ord.mastrino")}: <span className="font-mono">{mast?.account?.code}</span> {mast?.account?.name}</DialogTitle></DialogHeader>
          {mast && (
            <div className="space-y-3">
              <div className="flex gap-4 text-xs text-muted-foreground">
                <span>{t("ledger.debit")}: <b className="font-mono text-foreground">{fmt(mast.total_debit)}</b></span>
                <span>{t("ledger.credit")}: <b className="font-mono text-foreground">{fmt(mast.total_credit)}</b></span>
                <span>{t("ord.balance")}: <b className="font-mono text-foreground" data-testid="mastrino-balance">{fmt(mast.balance)}</b></span>
              </div>
              <Table>
                <TableHeader><TableRow><TableHead>{t("common.date")}</TableHead><TableHead>{t("common.description")}</TableHead><TableHead className="text-right">{t("ledger.debit")}</TableHead><TableHead className="text-right">{t("ledger.credit")}</TableHead><TableHead className="text-right">{t("ord.balance")}</TableHead></TableRow></TableHeader>
                <TableBody>
                  {mast.movements.map((m, i) => (
                    <TableRow key={i}><TableCell className="font-mono text-xs">{m.date}</TableCell><TableCell className="text-sm">{m.description}</TableCell>
                      <TableCell className="text-right font-mono text-xs">{m.debit ? fmt(m.debit) : ""}</TableCell><TableCell className="text-right font-mono text-xs">{m.credit ? fmt(m.credit) : ""}</TableCell>
                      <TableCell className="text-right font-mono text-xs">{fmt(m.balance)}</TableCell></TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
